import { useAuth } from '../auth/AuthContext'
import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import Spinner from '../components/Spinner'

const IS_LOCAL_DEV = import.meta.env.VITE_LOCAL_DEV === 'true'

function loginAsLocalUser(token, displayName, appRole) {
  sessionStorage.setItem('aipulse_token', token)
  sessionStorage.setItem(
    'aipulse_user',
    JSON.stringify({ display_name: displayName, app_role: appRole, team_id: null }),
  )
  window.location.href = '/experiments'
}

export default function Login() {
  const authContext = useAuth()
  const { user, loading } = authContext
  const { inProgress } = useMsal()
  const login = () => {
    sessionStorage.removeItem('aipulse_auth_error')
    authContext.login()
  }
  const navigate = useNavigate()
  const location = useLocation()
  const hasAuthError = location.state?.authError === true

  useEffect(() => {
    if (user) navigate('/experiments', { replace: true })
  }, [user, navigate])

  // Automatically trigger the Microsoft login redirect when the page loads in a
  // clean (non-error) state. Users coming from MyApps (or any other entry point)
  // who already have an active Azure AD session will be signed in silently by
  // Microsoft and land on /experiments without ever seeing this page.
  // The hasAuthError guard prevents an infinite redirect loop on login failure.
  useEffect(() => {
    if (!IS_LOCAL_DEV && !hasAuthError && !user && !loading && inProgress === InteractionStatus.None) {
      login()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // While MSAL is processing a redirect or the auth context is loading,
  // show a spinner instead of the login form to avoid a confusing flash.
  if (loading || inProgress !== InteractionStatus.None) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="lg" label="Signing in…" />
      </div>
    )
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <img src="/logo.png" alt="AI Pulse logo" className="login-logo" />
        <h1 className="login-title">AI Pulse</h1>
        <p className="login-tagline">Track AI tool effectiveness across experiments.</p>

        {hasAuthError && (
          <p style={{ color: 'var(--color-error, #c0392b)', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Sign-in failed. Please try again or contact support if the problem persists.
          </p>
        )}

        {IS_LOCAL_DEV ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => loginAsLocalUser('local-dev-admin', 'Local Admin', 'admin')}
              className="btn btn-dev-admin btn-lg"
              style={{ width: '100%' }}
            >
              Login as Local Admin
            </button>
            <button
              type="button"
              onClick={() => loginAsLocalUser('local-dev-user', 'Local User', 'user')}
              className="btn btn-dev-user btn-lg"
              style={{ width: '100%' }}
            >
              Login as Local User
            </button>
            <button
              type="button"
              onClick={() => loginAsLocalUser('local-dev-viewer', 'Local Viewer', 'viewer')}
              className="btn btn-dev-viewer btn-lg"
              style={{ width: '100%' }}
            >
              Login as Local Viewer
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={login}
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
          >
            Sign in with Microsoft
          </button>
        )}
      </div>
    </div>
  )
}

